import type { SectId, SkillDef, WeaponType } from '@jianghu/game-data';

export type CastRejectReason =
  | 'dead'
  | 'stunned'
  | 'busy'
  | 'dashing'
  | 'unknown_skill'
  | 'not_learned'
  | 'weapon'
  | 'no_target'
  | 'invalid_target'
  | 'out_of_range'
  | 'mana'
  | 'stamina'
  | 'cooldown';

export const CAST_REJECT_TEXT: Readonly<Record<CastRejectReason, string>> = {
  dead: 'You cannot do that while defeated.',
  stunned: 'You are stunned.',
  busy: 'You are still performing a technique.',
  dashing: 'You cannot use techniques while dodging.',
  unknown_skill: 'Unknown technique.',
  not_learned: 'You have not learned that technique yet.',
  weapon: 'That technique requires a different weapon.',
  no_target: 'No target selected.',
  invalid_target: 'Invalid target.',
  out_of_range: 'Target is out of range.',
  mana: 'Not enough qi.',
  stamina: 'Not enough stamina.',
  cooldown: 'That technique is not ready yet.',
};

export interface CastCheckInput {
  skill: SkillDef | undefined;
  casterSect: SectId | 'monster';
  casterLevel: number;
  alive: boolean;
  stunned: boolean;
  /** Remaining lock ticks from a previous technique. */
  rootTicks: number;
  dashing: boolean;
  weapon: WeaponType | null;
  mp: number;
  stamina: number;
  cooldownReady: boolean;
  /** Present when a target was supplied. */
  target?: { alive: boolean; hostile: boolean; distance: number; radius: number } | undefined;
}

/**
 * Pure validation of the skill execution pipeline's checks 1-5 (status, weapon, target, resources,
 * cooldown). The server runs it authoritatively; the client runs it to avoid sending doomed requests.
 */
export function validateCast(i: CastCheckInput): { ok: true } | { ok: false; reason: CastRejectReason } {
  const skill = i.skill;
  if (!skill) return { ok: false, reason: 'unknown_skill' };
  if (!i.alive) return { ok: false, reason: 'dead' };
  if (i.stunned) return { ok: false, reason: 'stunned' };
  if (i.rootTicks > 0) return { ok: false, reason: 'busy' };
  if (i.dashing) return { ok: false, reason: 'dashing' };
  if (skill.sect !== i.casterSect || i.casterLevel < skill.levelRequired)
    return { ok: false, reason: 'not_learned' };
  if (skill.weaponRequired && i.weapon !== skill.weaponRequired) return { ok: false, reason: 'weapon' };
  if (skill.requiresTarget) {
    if (!i.target) return { ok: false, reason: 'no_target' };
    if (!i.target.alive || !i.target.hostile) return { ok: false, reason: 'invalid_target' };
    if (i.target.distance - i.target.radius > skill.range + skill.validation.rangeTolerance) {
      return { ok: false, reason: 'out_of_range' };
    }
  }
  if (i.mp < skill.manaCost) return { ok: false, reason: 'mana' };
  if (i.stamina < skill.staminaCost) return { ok: false, reason: 'stamina' };
  if (!i.cooldownReady) return { ok: false, reason: 'cooldown' };
  return { ok: true };
}
